import { createContext, useContext } from 'react';

/**
 * Describes how a tile's preview image should behave when the tile is present
 * in the server HTML.
 *
 * By default `VideoPreview` renders its image at `opacity-0` and fades it in
 * from a JS `onLoad` handler, and marks it `loading="lazy"`. That makes the
 * first painted image depend on the whole download -> parse -> hydrate chain,
 * which is what pushed LCP on the grid pages to 4.5 s (DEV-6492).
 *
 * Every tile the server paints skips the fade-in, so its image is visible
 * without JS. Loading stays lazy for all of them except the LCP candidate,
 * which is the only tile switched to `loading="eager"` + `priority` — eager
 * loading the whole first screen of tiles would compete with it for bandwidth.
 */
export interface SsrTileImage {
  /** LCP candidate: preload it and fetch it eagerly at high priority. */
  isLcpCandidate: boolean;
  /** Skip the JS-driven fade-in so the image paints as soon as it decodes. */
  isServerPainted: boolean;
}

/** Client-rendered tile: lazy image, fade in once JS reports it loaded. */
export const CLIENT_PAINTED_TILE: SsrTileImage = {
  isLcpCandidate: false,
  isServerPainted: false,
};

/** In the server HTML, but not the LCP candidate — visible without JS. */
export const SSR_PAINTED_TILE: SsrTileImage = {
  isLcpCandidate: false,
  isServerPainted: true,
};

/** First tile of the first row — the LCP element on every grid page. */
export const SSR_LCP_TILE: SsrTileImage = {
  isLcpCandidate: true,
  isServerPainted: true,
};

export const SsrTileImageContext =
  createContext<SsrTileImage>(CLIENT_PAINTED_TILE);
SsrTileImageContext.displayName = 'SsrTileImageContext';

export const useSsrTileImage = (): SsrTileImage =>
  useContext(SsrTileImageContext);
